// ABOUTME: AJAX cart layer over the Shopify Cart API — getCart/addToCart/updateCart/removeFromCart
// + the window.atc(id) global, rebuilt on Horizon's native cart events and Section Rendering bundling.
//
// UTIL-CART (B2): the live theme's src/scripts/utils/Cart.js wired cart JSON into the bespoke
// window.setState bus and force-opened the React inline cart; the rebuy-widgets bridge is dropped.
// Here every mutation bundles the page's cart sections into the Cart API request (the `sections`
// param collected from `cart-items-component[data-section-id]`, exactly like the shipped
// product-form.js / component-cart-items.js) and announces itself with the theme's own event
// classes from @theme/events — so the native drawer, cart page, bubble, and any jf- glue
// (e.g. jf-recharge-cart) re-render without a reload. The post-add drawer open is native:
// cart-drawer-component hears the standard shopify:cart:lines-update (action 'add') dispatched
// below and opens its <theme-drawer> when settings.auto_open_cart_drawer is on — the live
// layer's cartOpen=true contract carried by the setting, not by a reach-in.
//
// The module keeps its top level import-free so the operator math is unit-testable in plain node
// (scripts/horizon-migrate/utilcart_unit_test.mjs); browser wiring dynamically imports
// '@theme/events' and '@theme/standard-events' through the page importmap.
//
// Standard storefront events (R4): every mutation the operator runs also dispatches the platform
// contract — shopify:cart:lines-update / note-update ahead of the request (deferred promise,
// resolved with the AJAX cart via createCartFromAjaxResponse) and shopify:cart:error on failure.
// The @theme/events announce below stays the theme-internal render path; the shopify:* events are
// the public integration surface. If the standard-events library cannot load, mutations proceed
// and only the standard dispatch is skipped.

/**
 * The UTIL-CART "latent operator bug" fix. The live updateCart items branch read an
 * undeclared `operator` identifier and threw ReferenceError whenever `items` was passed;
 * the operator is now an explicit, validated argument with the legacy-intended math.
 *
 * @param {Array<{key: string, quantity: number, add_qty: number}>} items - Cart lines with
 *   their current quantity and the delta to apply.
 * @param {'add'|'subtract'|'remove'} operator - add -> quantity + add_qty;
 *   subtract -> quantity - add_qty; remove -> 0.
 * @returns {Record<string, number>} The /cart/update.js `updates` map (line key -> new quantity).
 */
export const computeLineItemUpdates = (items, operator) => {
  const operators = {
    add: (item) => item.quantity + item.add_qty,
    subtract: (item) => item.quantity - item.add_qty,
    remove: () => 0,
  };
  const apply = operators[operator];
  if (!apply) {
    throw new TypeError(
      `jf-cart updateCart: operator required with items — one of ${Object.keys(operators).join('|')} (got ${JSON.stringify(operator)})`
    );
  }
  const updates = {};
  for (const item of items) {
    updates[item.key] = apply(item);
  }
  return updates;
};

const routes = () => (typeof window !== 'undefined' && window.Theme?.routes) || {};

/** @returns {string[]} Section ids of every cart surface on the page (drawer + cart page). */
const cartSectionIds = () => {
  const ids = new Set();
  for (const component of document.querySelectorAll('cart-items-component')) {
    if (component instanceof HTMLElement && component.dataset.sectionId) {
      ids.add(component.dataset.sectionId);
    }
  }
  return Array.from(ids);
};

/** @returns {{sections?: string, sections_url?: string}} Section Rendering bundle params. */
const sectionBundle = () => {
  const ids = cartSectionIds();
  if (ids.length === 0) return {};
  return { sections: ids.join(','), sections_url: window.location.pathname };
};

/**
 * @param {string} url
 * @param {Object} body
 * @returns {Promise<Object>} Parsed JSON response.
 */
const postJson = async (url, body) => {
  const response = await fetch(url, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
    body: JSON.stringify(body),
  });
  // The Cart API answers errors as JSON with a `status` field; anything else that is not OK
  // (a 5xx page, a challenge) has no usable body and is surfaced as a transport error.
  try {
    return await response.json();
  } catch (error) {
    throw new Error(`jf-cart: ${url} answered ${response.status} without JSON`);
  }
};

/**
 * @param {string} operation
 * @param {Object} response - A Cart API error payload ({status, message, description}).
 * @returns {Error & {cartResponse: Object}}
 */
const cartApiError = (operation, response) =>
  Object.assign(new Error(`jf-cart ${operation}: ${response.message} — ${response.description}`), { cartResponse: response });

/**
 * Settles a failed mutation on every channel exactly once: the private cart:error bus, the
 * standard event's promise (rejected, so subscribers never wait forever) and shopify:cart:error.
 * Transport and parse failures reach here too, not only Cart API error payloads.
 * @param {{resolve: Function, reject: Function}} std
 * @param {Error & {cartResponse?: Object}} error
 * @param {boolean} [standard=true] - Also dispatch the standard error event.
 */
const failStandard = async (std, error, standard = true) => {
  const message = error.cartResponse?.message || error.message || 'Cart update failed';
  await announceError('jf-cart', error.cartResponse || { message, description: '', errors: message });
  std.reject(error);
  if (standard) await announceStandardError(message);
};

/**
 * Announces a cart mutation with the theme's own event class so every native listener
 * (cart-drawer-component, cart-items-component, cart-icon, product-form-component)
 * and jf- glue reacts exactly as it does to a core-initiated mutation.
 * @param {Object} cart - The full cart JSON.
 * @param {Record<string, string>} [sections] - Rendered section HTML from the Cart API bundle.
 */
const announce = async (cart, sections) => {
  const { CartUpdateEvent } = await import('@theme/jf-cart-events');
  document.dispatchEvent(
    new CartUpdateEvent(cart, 'jf-cart', {
      source: 'jf-cart',
      itemCount: cart.item_count,
      sections,
    })
  );
};

/**
 * @param {string} sourceId
 * @param {Object} errorResponse - Cart API error payload ({status, message, description, errors}).
 */
const announceError = async (sourceId, errorResponse) => {
  const { CartErrorEvent } = await import('@theme/jf-cart-events');
  document.dispatchEvent(
    new CartErrorEvent(sourceId, errorResponse.message, errorResponse.description, errorResponse.errors)
  );
};

/** Loads the standard-events library; a CDN failure degrades to skipped standard dispatch. */
const standardEvents = () =>
  import('@theme/standard-events').catch((error) => {
    console.debug('jf-cart: @theme/standard-events unavailable — standard dispatch skipped', error);
    return null;
  });

/**
 * @returns {Element} The element standard cart events dispatch from. Core's cart-items-component
 *   ignores a lines-update event whose target is itself (the mutation owner renders itself), so an
 *   operator mutation must never dispatch from it: the drawer wrapper where the drawer renders,
 *   else document.body (the cart page hosts a bare cart-items-component). Either way every
 *   cart-items-component on the page morphs from the bundled sections the settled promise carries.
 */
const standardCartTarget = () => document.querySelector('cart-drawer-component') || document.body;

/**
 * @returns {Element|undefined} The product form an operator-initiated add dispatches from.
 *   cart-drawer-component reads the dispatch target's `dialog:modal` ancestry to decide whether
 *   to auto-open now or wait for that dialog's close, so a form inside an open modal (a quick-add
 *   dialog) must never stand in for an add that did not come from it. With no form outside one,
 *   the add falls back to the cart target, which is what such an add is.
 */
const standardAddTarget = () =>
  Array.from(document.querySelectorAll('product-form-component')).find((form) => !form.closest('dialog:modal'));

/** No-op finishers for when the standard-events library is unavailable. */
const NOOP_FINISHERS = { resolve: () => {}, reject: () => {} };

/**
 * @param {Object} mod - The standard-events module.
 * @param {{resolve: Function, reject: Function}} deferred
 * @returns {{resolve: (ajaxCart: Object) => void, reject: (error: Error) => void}} Finishers that
 *   settle the event's deferred promise with the AJAX cart converted to the standard shape.
 */
const standardFinishers = (mod, deferred) => ({
  resolve: (ajaxCart) => deferred.resolve({
    cart: mod.CartLinesUpdateEvent.createCartFromAjaxResponse(ajaxCart),
    // Core cart surfaces read detail.sections off the settled promise; handing over the
    // bundle this mutation already fetched spares them a redundant section re-render.
    detail: { sections: ajaxCart?.sections },
  }),
  reject: (error) => deferred.reject(error),
});

/**
 * Dispatches shopify:cart:lines-update ahead of a mutation (deferred-promise pattern).
 * @param {Object} config
 * @param {'add'|'update'|'remove'} config.action
 * @param {Array<Object>} config.lines - Standard line shapes ({merchandiseId, quantity} for adds;
 *   {id, quantity} for updates/removes — ids are AJAX line keys on this theme).
 * @param {'product'|'cart'|'dialog'} config.context
 * @param {Element} [config.target]
 * @returns {Promise<{resolve: Function, reject: Function}>}
 */
const announceStandardLines = async ({ action, lines, context, target }) => {
  const mod = await standardEvents();
  if (!mod) return NOOP_FINISHERS;
  const deferred = mod.CartLinesUpdateEvent.createPromise();
  (target || standardCartTarget()).dispatchEvent(
    new mod.CartLinesUpdateEvent({ action, context, lines, promise: deferred.promise })
  );
  return standardFinishers(mod, deferred);
};

/**
 * @param {string} note
 * @returns {Promise<{resolve: Function, reject: Function}>}
 */
const announceStandardNote = async (note) => {
  const mod = await standardEvents();
  if (!mod) return NOOP_FINISHERS;
  const deferred = mod.CartNoteUpdateEvent.createPromise();
  standardCartTarget().dispatchEvent(
    new mod.CartNoteUpdateEvent({ context: 'cart', note, promise: deferred.promise })
  );
  return standardFinishers(mod, deferred);
};

/**
 * @param {string} message
 * @param {string} [code] - A Storefront API CartErrorCode value.
 */
const announceStandardError = async (message, code = 'INVALID') => {
  const mod = await standardEvents();
  if (!mod) return;
  standardCartTarget().dispatchEvent(new mod.CartErrorEvent({ error: message, code }));
};

/**
 * Fetches the cart. Pure read by default; mutations announce themselves, so `notify`
 * exists for callers that need to force the cart surfaces to re-render from a read.
 * @param {boolean} [notify=false] - Dispatch cart:update with the fetched cart.
 * @returns {Promise<Object>} The cart JSON.
 */
export const getCart = async (notify = false) => {
  const url = `${routes().cart_url || '/cart'}.js`;
  const cart = await fetch(url, { headers: { Accept: 'application/json' } }).then((response) => response.json());
  if (notify) await announce(cart);
  return cart;
};

/**
 * Adds items to the cart and morphs every cart surface from the bundled sections. The cart
 * drawer opens itself off the standard lines-update event this function dispatches
 * (cart-drawer-component auto-open, gated by settings.auto_open_cart_drawer). On a Cart API
 * error it dispatches cart:error and rejects; the error path never resolves the standard
 * event's promise, so the drawer stays closed.
 * @param {Object} config
 * @param {number|string} [config.id] - Variant id (single-item form).
 * @param {number} [config.quantity=1]
 * @param {Object} [config.properties={}] - Line item properties.
 * @param {number|string|false} [config.sellingPlan=false] - Selling plan id.
 * @param {Array<Object>|false} [config.items=false] - Full items array (overrides id/quantity).
 * @param {Record<string, string>|false} [config.attributes=false] - Cart attributes the add sets as well, for a caller that goes
 *   straight on to checkout and cannot wait for a separate write. The add endpoint merges them into the cart's attributes the way
 *   /cart/update.js does; the Ajax API reference does not list the parameter, so a caller that must have them also writes them
 *   through updateCart.
 * @returns {Promise<Object>} The cart JSON after the add.
 */
export const addToCart = async ({ id, quantity = 1, properties = {}, sellingPlan = false, items = false, attributes = false } = {}) => {
  const item = { id, quantity };
  if (Object.keys(properties).length > 0) item.properties = { ...properties };
  if (sellingPlan) item.selling_plan = sellingPlan;

  const requestItems = items || [item];
  const productForm = standardAddTarget();
  const std = await announceStandardLines({
    action: 'add',
    context: productForm ? 'product' : 'cart',
    lines: requestItems.map((line) => ({ merchandiseId: Number(line.id), quantity: line.quantity ?? 1 })),
    target: productForm || undefined,
  });

  try {
    const response = await postJson(routes().cart_add_url || '/cart/add.js', {
      items: requestItems,
      ...(attributes ? { attributes } : {}),
      ...sectionBundle(),
    });
    if (response.status) throw cartApiError('addToCart', response);

    const cart = await getCart();
    await announce(cart, response.sections);
    // /cart.js carries no section bundle, and standardFinishers reads detail.sections off what
    // it is handed: without the add's own bundle the native cart surfaces re-fetch sections this
    // call already has, so the drawer morphs twice.
    std.resolve({ ...cart, sections: response.sections });
    return cart;
  } catch (error) {
    await failStandard(std, error);
    throw error;
  }
};

/**
 * Updates line quantities, cart attributes, or the cart note via /cart/update.js.
 * Signature-compatible with the live layer, plus the now-required `operator`
 * when `items` is passed (see computeLineItemUpdates).
 * @param {Object} config
 * @param {string|false} [config.key=false] - Line item key to set to `quantity`.
 * @param {number|string|false} [config.variantID=false] - Variant id to set to `quantity`.
 * @param {number} [config.quantity=0]
 * @param {Object|false} [config.attributes=false] - Cart attributes.
 * @param {string|false} [config.note=false] - Cart note.
 * @param {Array<Object>|false} [config.items=false] - Lines for operator-based updates.
 * @param {'add'|'subtract'|'remove'} [config.operator] - Required with `items`.
 * @returns {Promise<Object>} The cart JSON after the update.
 */
export const updateCart = async ({
  key = false,
  variantID = false,
  quantity = 0,
  attributes = false,
  note = false,
  items = false,
  operator,
} = {}) => {
  const updatesBody = { updates: {} };
  if (items) {
    updatesBody.updates = computeLineItemUpdates(items, operator);
  } else {
    if (variantID) updatesBody.updates[variantID] = quantity;
    if (attributes) updatesBody.attributes = attributes;
    if (key) updatesBody.updates[key] = quantity;
    if (note) updatesBody.note = note;
  }

  const lineEntries = Object.entries(updatesBody.updates);
  let std = NOOP_FINISHERS;
  if (lineEntries.length > 0) {
    std = await announceStandardLines({
      action: lineEntries.every(([, lineQuantity]) => lineQuantity === 0) ? 'remove' : 'update',
      context: 'cart',
      lines: lineEntries.map(([lineId, lineQuantity]) => ({ id: lineId, quantity: lineQuantity })),
    });
  } else if (updatesBody.note !== undefined) {
    std = await announceStandardNote(updatesBody.note);
  }

  try {
    const cart = await postJson(routes().cart_update_url || '/cart/update.js', {
      ...updatesBody,
      ...sectionBundle(),
    });
    if (cart.status) throw cartApiError('updateCart', cart);

    await announce(cart, cart.sections);
    std.resolve(cart);
    return cart;
  } catch (error) {
    await failStandard(std, error);
    throw error;
  }
};

/**
 * Removes a line from the cart via /cart/change.js.
 * @param {{key: string}} lineItem - The cart line (its `key` identifies the line).
 * @param {boolean} [notify=true] - Announce the mutation (the live layer's setState flag:
 *   Recharge's swap removed silently before re-adding).
 * @returns {Promise<Object>} The cart JSON after the removal.
 */
export const removeFromCart = async (lineItem, notify = true) => {
  // notify=false is an intentional silent step inside a larger flow (the Recharge swap's
  // remove-then-re-add) — the flow's own announce is the single mutation event, so the
  // standard dispatch is skipped along with the private one.
  const std = notify
    ? await announceStandardLines({
        action: 'remove',
        context: 'cart',
        lines: [{ id: lineItem.key, quantity: 0 }],
      })
    : NOOP_FINISHERS;

  try {
    const cart = await postJson(routes().cart_change_url || '/cart/change.js', {
      id: lineItem.key,
      quantity: 0,
      ...(notify ? sectionBundle() : {}),
    });
    if (cart.status) throw cartApiError('removeFromCart', cart);

    if (notify) await announce(cart, cart.sections);
    std.resolve(cart);
    return cart;
  } catch (error) {
    await failStandard(std, error, notify);
    throw error;
  }
};

/**
 * Changes one line in place via /cart/change.js — quantity and/or selling plan
 * (the Recharge swap primitive: a plan id subscribes the line, null clears it back
 * to one-time; Shopify merges into an existing line with the same variant + plan).
 * @param {Object} change
 * @param {string} change.key - The cart line's `key`.
 * @param {number} [change.quantity] - New quantity. Pass the line's current quantity to keep it: a
 *   change request without one leaves the line at 1 (observed on the Cart API 2026-09-09).
 * @param {number|string|null} [change.sellingPlan=null] - Selling plan id; null clears it.
 * @returns {Promise<Object>} The cart JSON after the change.
 */
export const changeLine = async ({ key, quantity, sellingPlan = null }) => {
  const std = await announceStandardLines({
    action: 'update',
    context: 'cart',
    lines: [{ id: key, ...(quantity !== undefined ? { quantity } : {}) }],
  });

  try {
    const cart = await postJson(routes().cart_change_url || '/cart/change.js', {
      id: key,
      ...(quantity !== undefined ? { quantity } : {}),
      selling_plan: sellingPlan === null || sellingPlan === false ? '' : sellingPlan,
      ...sectionBundle(),
    });
    if (cart.status) throw cartApiError('changeLine', cart);

    await announce(cart, cart.sections);
    std.resolve(cart);
    return cart;
  } catch (error) {
    await failStandard(std, error);
    throw error;
  }
};

/**
 * Re-renders every cart surface from current server state: fetches the cart plus the page's
 * bundled cart sections and announces on the private bus so core components morph. Used after
 * a standard action mutates the cart out-of-band (a configured updateCart skips the runtime's
 * own page refresh). Not a mutation — dispatches no standard event.
 * @returns {Promise<Object>} The cart JSON.
 */
export const refreshCartSections = async () => {
  const cart = await getCart();
  const ids = cartSectionIds();
  if (ids.length === 0) {
    await announce(cart);
    return cart;
  }
  const sections = await fetch(`${window.location.pathname}?sections=${ids.join(',')}`, {
    headers: { Accept: 'application/json' },
  }).then((response) => response.json());
  await announce(cart, sections);
  return cart;
};

// DATED ADAPTERS (2026-07-10): window.atc and window.JFCart are the compatibility surface for
// legacy callers (Liquid onclick handlers, Recharge glue, buy-box code). New integrations use
// the standard storefront contract instead — Shopify.actions to mutate, shopify:cart:* to listen.
// This surface is frozen: additions land on the standard contract, never here.
if (typeof window !== 'undefined') {
  window.atc = (id) => addToCart({ id, quantity: 1 });
  window.JFCart = { getCart, addToCart, updateCart, changeLine, removeFromCart, computeLineItemUpdates };
}
